import { useState, useEffect } from "react";

export default function CreatorApplicationForm({ user }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [applicationStatus, setApplicationStatus] = useState(null);
  const [checking, setChecking] = useState(true);
const [error, setError] = useState("");
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    location: "",
    primaryPlatform: "",

    youtubeChannel: "",
    youtubeLink: "",
    subscribers: "",

    instagramHandle: "",
    instagramLink: "",
    followers: "",

    pitch: "",
    sampleLinks: "",
  });

  // ✅ Autofill
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        fullName: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  // ✅ Check status
  useEffect(() => {
    const checkApplication = async () => {
      try {
        const token =
          sessionStorage.getItem("token") ||
          localStorage.getItem("token");

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/curator/my-application`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = await res.json();

        if (data.applied) {
          setApplicationStatus(data.status);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setChecking(false);
      }
    };

    checkApplication();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const next = () => setStep(step + 1);
  const prev = () => setStep(step - 1);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const token =
        sessionStorage.getItem("token") ||
        localStorage.getItem("token");

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/curator/apply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(form),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setError(data.message);
        return;
      }

      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
  if (error) {
    const timer = setTimeout(() => {
      setError("");
    }, 4000);

    return () => clearTimeout(timer);
  }
}, [error]);

  if (checking) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-8 h-8 border-4 border-[#5b6f00]/30 border-t-[#5b6f00] rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium animate-pulse">Checking status...</p>
        </div>
      </div>
    );
  }

  if (applicationStatus) {
    return (
      <div className="max-w-xl mx-auto w-full mt-8 p-10 bg-white rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center text-center space-y-6">
        <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-2">
          {applicationStatus === "pending" && <span className="text-3xl">⏳</span>}
          {applicationStatus === "approved" && <span className="text-3xl">🎉</span>}
          {applicationStatus === "rejected" && <span className="text-3xl">❌</span>}
        </div>
        
        <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Application Status</h2>

        {applicationStatus === "pending" && (
          <div className="space-y-2">
            <p className="text-[#5b6f00] font-medium px-4 py-1.5 bg-[#5b6f00]/10 rounded-full inline-block">Under Review</p>
            <p className="text-gray-500 text-sm mt-4">We are currently reviewing your profile. We'll get back to you shortly.</p>
          </div>
        )}

        {applicationStatus === "approved" && (
          <div className="space-y-2">
            <p className="text-green-700 font-medium px-4 py-1.5 bg-green-50 rounded-full inline-block">Approved</p>
            <p className="text-gray-500 text-sm mt-4">Welcome to the creator community! You can now start creating itineraries.</p>
          </div>
        )}

        {applicationStatus === "rejected" && (
          <div className="space-y-2">
            <p className="text-red-700 font-medium px-4 py-1.5 bg-red-50 rounded-full inline-block">Declined</p>
            <p className="text-gray-500 text-sm mt-4">Unfortunately, your application was not approved at this time. You may apply again later.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto w-full bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 my-8">
      
      {!success && (
        <div className="mb-10">
          {/* <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-[#5b6f00]/5 rounded-2xl flex items-center justify-center text-2xl">
              🌍
            </div>
          </div> */}
          <h1 className="text-2xl sm:text-3xl font-semibold text-center text-gray-900 tracking-tight mb-3">
            Become a Travel Creator
          </h1>
          <p className="text-center text-gray-500 text-sm sm:text-base mb-8">
            Turn your travel experience into real itineraries
          </p>
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-center space-x-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                    step === s 
                      ? "bg-[#5b6f00] text-white" 
                      : step > s 
                        ? "bg-[#5b6f00]/20 text-[#5b6f00]" 
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {step > s ? "✓" : s}
                </div>
                {s < 3 && (
                  <div 
                    className={`w-12 h-1 rounded-full mx-2 transition-colors ${
                      step > s ? "bg-[#5b6f00]/20" : "bg-gray-100"
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {success ? (
        <div className="flex flex-col items-center py-8 space-y-6 text-center">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center">
            <span className="text-4xl text-green-500">✅</span>
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              Application Submitted
            </h2>
            <p className="text-gray-500">
              Thank you for applying. We'll review your application within 24–48 hours.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Full Name</label>
                <input
                  value={form.fullName}
                  disabled
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed transition-all"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Email Address</label>
                <input
                  value={form.email}
                  disabled
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Location</label>
                <input
                  name="location"
                  placeholder="e.g. New York, USA"
                  value={form.location}
                  onChange={handleChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                />
              </div>

              <div className="pt-4">
                <button
                  onClick={next}
                  className="w-full bg-[#5b6f00] text-white font-medium p-4 rounded-xl shadow-sm shadow-[#5b6f00]/20 hover:bg-[#4a5a00] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5b6f00]"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Primary Platform</label>
                <select
                  name="primaryPlatform"
                  value={form.primaryPlatform}
                  onChange={handleChange}
                  required
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300 appearance-none"
                  style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%236B7280\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1rem center', backgroundSize: '1.5em 1.5em' }}
                >
                  <option value="" disabled>Select your main platform</option>
                  <option value="youtube">YouTube</option>
                  <option value="instagram">Instagram</option>
                  <option value="both">Both</option>
                </select>
              </div>

              {/* INSTAGRAM SECTION */}
              {(form.primaryPlatform === "instagram" || form.primaryPlatform === "both") && (
                <div className="p-5 border border-gray-100 bg-gray-50/50 rounded-2xl space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-pink-600 bg-pink-100 p-1.5 rounded-lg">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                    </span>
                    <h3 className="font-medium text-gray-900">Instagram Details</h3>
                  </div>

                  <div className="space-y-3">
                    <input
                      name="instagramHandle"
                      placeholder="@username"
                      value={form.instagramHandle}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />

                    <input
                      name="instagramLink"
                      placeholder="Profile URL"
                      value={form.instagramLink}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />

                    <input
                      name="instagramFollowers"
                      placeholder="Number of followers (e.g. 10k)"
                      value={form.instagramFollowers}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />
                  </div>
                </div>
              )}

              {/* YOUTUBE SECTION */}
              {(form.primaryPlatform === "youtube" || form.primaryPlatform === "both") && (
                <div className="p-5 border border-gray-100 bg-gray-50/50 rounded-2xl space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-red-600 bg-red-100 p-1.5 rounded-lg">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
                    </span>
                    <h3 className="font-medium text-gray-900">YouTube Details</h3>
                  </div>

                  <div className="space-y-3">
                    <input
                      name="youtubeChannel"
                      placeholder="Channel Name"
                      value={form.youtubeChannel}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />

                    <input
                      name="youtubeLink"
                      placeholder="Channel URL"
                      value={form.youtubeLink}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />

                    <input
                      name="youtubeSubscribers"
                      placeholder="Number of subscribers (e.g. 5k)"
                      value={form.youtubeSubscribers}
                      onChange={handleChange}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300"
                    />
                  </div>
                </div>
              )}
              {error && (
  <div className="flex items-start gap-3 p-4 rounded-2xl border border-red-100 bg-red-50 text-red-700 text-sm">
    
    <div className="mt-0.5">
      ⚠️
    </div>

    <div>
      <p className="font-medium">Required field missing</p>
      <p className="text-red-600/80 mt-0.5">
        {error}
      </p>
    </div>

  </div>
)}

              {/* BUTTONS */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={prev}
                  className="w-1/3 bg-white text-gray-700 border border-gray-200 font-medium p-4 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-all hover:border-gray-300 shadow-sm"
                >
                  Back
                </button>

                <button
                  onClick={() => {
                   if (!form.primaryPlatform) {
                      setError("Please select your primary platform");
                      return;
                    }

                    setError("");
                    next();
                  }}
                  className="w-2/3 bg-[#5b6f00] text-white font-medium p-4 rounded-xl shadow-sm shadow-[#5b6f00]/20 hover:bg-[#4a5a00] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5b6f00]"
                >
                  Continue
                </button>
              </div>

            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700 flex justify-between">
                  <span>Your Travel Pitch</span>
                  <span className="text-gray-400 font-normal">Required</span>
                </label>
                <textarea
                  name="pitch"
                  placeholder="Tell us about your travel style, destinations you love, and why you'd be a great creator..."
                  value={form.pitch}
                  onChange={handleChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300 resize-none h-32"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">Sample Content Links</label>
                <textarea
                  name="sampleLinks"
                  placeholder="Paste links to your best Instagram posts, reels, YouTube videos, or blogs..."
                  value={form.sampleLinks}
                  onChange={handleChange}
                  className="w-full p-3.5 border border-gray-200 rounded-xl bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5b6f00]/20 focus:border-[#5b6f00] transition-all hover:border-gray-300 resize-none h-24"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={prev}
                  disabled={loading}
                  className="w-1/3 bg-white text-gray-700 border border-gray-200 font-medium p-4 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-all hover:border-gray-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-2/3 flex items-center justify-center gap-2 bg-[#5b6f00] text-white font-medium p-4 rounded-xl shadow-sm shadow-[#5b6f00]/20 hover:bg-[#4a5a00] hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5b6f00] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Application</span>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
